import { useState, useEffect, useCallback, useRef } from 'react'
import cx from 'classnames'
import { Col } from 'antd'
import _ from 'lodash'
import UiStateContainer, { defaultUiContextCosumer } from '@vzmi/ec-react-ui-state-container'
import { numeralFormat } from '@vzmi/component_ec_nodejs_util/dist/lib/renderUtil'
import { Modal, FormLoader, PopoverImg } from '@vzmi/shp_shared_compo'
import { CDN } from '@vzmi/shp_shared_compo/dist/constants/cdn.js'
import { formDecorator, FormValueSelector } from '../../../../../Common/forms'
import Information from '../../../../../Common/Information'
import YPopover from '../../../../../Common/Popover'
import { IconPopoverLabel, LimitDescBtwCostAndStock } from '../../../../Common/ProposalForm/FormLabelTooltip/FormLabelTooltip'
import ImageFunctionTable from '../../../../UpdateListing/ImageFunctionTable/ImageFunctionTable'
import CustomProductIdCol from './CustomProductIdCol'
import CustomCols from './CustomCols'
import { PRODUCT_KEY } from '../../constants'
import styles from './styles.css'
import { getAttrLevelFromListing } from '../../../helpers/getters'
import { getAttrMap, getFakeProductId } from '../../helpers'
import { getUploadStateByUI } from '../../../../Common/ProposalForm/Shared/util'
import { findFormErrorElements } from '../../../../../../lib/FormHelper'
import NewProductBtn from '../../NewProductBtn'

export const REQUIRED_MSG = '必填欄位'

export const requiredValidate = values => {
  const errors = {}
  const models = _.get(values, 'models', [])
  for (const [index, model] of models.entries()) {
    const { attrLevel } = model
    // 驗證單雙層屬性、商品名稱不可為空
    const fields = [
      PRODUCT_KEY.ATTR,
      PRODUCT_KEY.SPEC_SELECTED_VALUE,
      ...((attrLevel === 2)
        ? [
          PRODUCT_KEY.ITEM_ATTR,
          PRODUCT_KEY.ITEM_SPEC_SELECTED_VALUE
        ]
        : []
      )
    ]
    for (const field of fields) {
      const fieldValue = _.get(model, field)
      if (_.isNil(fieldValue)) {
        _.set(errors, `models[${index}].${field}`, REQUIRED_MSG)
      }
    }

    const imagesValue = _.get(model, PRODUCT_KEY.NEW_PRODUCT_IMAGES)
    const specSelectedValue = _.get(model, PRODUCT_KEY.SPEC_SELECTED_VALUE)
    // 驗證圖片需要先選規格且不可為空
    if (_.isEmpty(imagesValue)) {
      const message = _.isEmpty(specSelectedValue) ? '請先選擇規格' : '請上傳商品圖片'
      _.set(errors, `models[${index}].${PRODUCT_KEY.NEW_PRODUCT_IMAGES}`, message)
    }
  }

  return errors
}

// 產生一個預設商品
export const getNewProduct = (initiallyFetchedListing = {}, sortList, dataMap, form = {}) => {
  const {
    listingSpecName = '',
    listingItemSpecName = '',
    shareMediaBetweenModels
  } = form
  const attrLevel = getAttrLevelFromListing(initiallyFetchedListing)
  const isTwoLayerListing = (attrLevel === 2)
  const editable = isTwoLayerListing
    ? true
    : !shareMediaBetweenModels
  // 單層共用商品圖在新增商品時會直接給他共用的商品圖
  const firstRowImages = (!isTwoLayerListing && shareMediaBetweenModels)
    ? getMaxSizeImagesFromDataMap(dataMap, _.first(sortList))
    : []
  const newProductId = Date.now()
  const product = {
    // 用於 imageTable 判斷上傳圖片(imageFunction)是否可以使用
    isSpecSelected: false,
    attrLevel,
    // 新增商品時指判斷單層是否為共用商品圖(雙層需要等到選第一層屬性後才做判斷)
    // 共用商品圖的時候不允許展開商品圖
    [PRODUCT_KEY.EDITABLE]: editable,
    [PRODUCT_KEY.IS_NEW]: true,
    [PRODUCT_KEY.NEW_PRODUCT_IMAGES]: firstRowImages,
    [PRODUCT_KEY.SPEC_NAME]: listingSpecName,
    [PRODUCT_KEY.SPEC_VISIBLE]: true,
    // 即時產生一個 id 用於判別是新的商品以及更新商品
    [PRODUCT_KEY.ID]: getFakeProductId(newProductId),
    ...(isTwoLayerListing && ({
      // 即時產生一個 id 用於判別是新的商品以及更新商品
      [PRODUCT_KEY.ITEM_ID]: getFakeProductId(newProductId),
      [PRODUCT_KEY.ITEM_SPEC_NAME]: listingItemSpecName,
      [PRODUCT_KEY.ITEM_SPEC_VISIBLE]: true
    }))
  }
  return product
}

export const getMaxSizeImagesFromDataMap = (dataMap, spec) => {
  const images = _.flow(
    // 為了取有小數點的欄位分兩次取得比較容易
    // ex: dataMap['5.1'][0].images
    () => _.get(dataMap, [`${spec}`], []),
    // dataMap 源自 API 欄位，圖片放在 images
    product => _.get(product, `[0].${PRODUCT_KEY.IMAGES}`, []),
    images => _.filter(images, image => image.height === 1000)
  )()
  return images
}

export const getUpdatedProduct = (initiallyFetchedListing, form) => product => {
  const {
    models: originModels
  } = initiallyFetchedListing
  const {
    shareMediaBetweenModels
  } = form
  const attrLevel = getAttrLevelFromListing(initiallyFetchedListing)
  const isTwoLayerListing = (attrLevel === 2)
  const originModelMapBySku = _.keyBy(originModels, 'sku')
  const attributes = _.get(product, PRODUCT_KEY.ATTRIBUTES, [])
  const updatedProduct = {
    // 移除 index 欄位才不會讓 imageTable 當作非第一列要隱藏圖片 component
    ..._.omit(product, ['index']),
    [PRODUCT_KEY.NEW_PRODUCT_IMAGES]: _.flow(
      () => _.get(product, PRODUCT_KEY.IMAGES, []),
      // 按照 order 由小排到大，每個 order 按照圖片大小由大排到小
      images => _.orderBy(images, ['order', 'height'], ['asc', 'desc']),
      // 每個 order 取的一筆
      sortedImages => _.uniqBy(sortedImages, 'order'),
      // 移除 status 避免 antd status 更新不完整導致已上傳完成圖片被判斷成上傳中
      uniqImages => _.map(uniqImages, uniqImage => _.omit(uniqImage, ['status']))
    )(),
    // 重新計算轉換 attributes 成跳出視窗內選擇的屬性值
    // attributes 只有送出提案後才會有，用在確認、檢視頁用來看跳出視窗的商品資訊
    ..._.reduce(attributes, (attrs, attribute, index) => {
      const attrMap = getAttrMap(index === 1)
      attrs[attrMap.attr] = _.get(attribute, 'values', [])
      return attrs
    }, {}),
    editable: !(
      // 單層判斷是否賣場為共用商品圖
      (!isTwoLayerListing && shareMediaBetweenModels) ||
      // 雙層判斷選擇的屬性是否為既有屬性
      (isTwoLayerListing && (product[PRODUCT_KEY.ID] in originModelMapBySku))
    )
  }
  return updatedProduct
}

const FORM_CONFIG = {
  form: 'NEW_PRODUCT_MODAL_FORM',
  validate: requiredValidate,
  initialValues: {
    models: []
  }
}

const ProductNameTitleWtihPopover = () => {
  return (
    <IconPopoverLabel
      popoverPlacement='right'
      className={styles.productNameTitle}
      popoverContent={(
        <>
          <p>用於顯示在賣場頁上的屬性選項名稱</p>
          <PopoverImg
            url={`${CDN}/supplier/newProductNameTip.png`}
            width={619}
            height={500}
          />
        </>
      )}
    >
      項目顯示名稱
    </IconPopoverLabel>
  )
}

const NewProductModel = props => {
  const {
    updateListingModelForm: {
      initiallyFetchedListing = {},
      listing: form = {}
    } = {},
    specDetailMapBySpec,
    dataMap,
    sortList,
    products = [],
    change,
    handleSubmit,
    onOk,
    visible,
    submitFailed,
    readOnly = false,
    ui
  } = props
  const {
    listingSpecName = '',
    listingItemSpecName = ''
  } = form
  const { isMediaUploading } = getUploadStateByUI(ui)
  const [imageTableKey, setImageTableKey] = useState(Date.now())
  const {
    name: listingName,
    models = []
  } = initiallyFetchedListing
  const shareMediaBetweenModels = _.get(form, 'shareMediaBetweenModels')
  const isTwoLayerListing = !_.isEmpty(listingItemSpecName)

  const updateModels = useCallback(
    newModels => change('models', newModels),
    [change]
  )

  const getNewProductWithCallback = useCallback(
    getNewProduct,
    [shareMediaBetweenModels, isTwoLayerListing, sortList, dataMap, initiallyFetchedListing, listingSpecName, listingItemSpecName]
  )

  const addNewProduct = (modelsFromFormValues) => () => {
    const newModels = [...modelsFromFormValues, getNewProduct(initiallyFetchedListing, sortList, dataMap, form)]
    updateModels(newModels)
  }

  const deleteProduct = (index, modelsFromFormValues) => () => {
    const newModels = _.flow(
      () => _.filter(modelsFromFormValues, (model, modelIndex) => {
        // index 來自 onChange spec field 過濾出來的，所以會是字串格式
        return _.toNumber(index) !== modelIndex
      }),
      // 如果最後一筆刪除則預設產生一筆預設商品列
      filteredModels => _.size(filteredModels) === 0
        ? [getNewProduct(initiallyFetchedListing, sortList, dataMap, form)]
        : filteredModels
    )()
    updateModels(newModels)
  }

  const submitProduct = async (...args) => {
    handleSubmit(onOk)(...args)
    const ele = _.first(await findFormErrorElements())
    if (_.isEmpty(ele)) {
      return
    }

    // 找到有錯誤訊息時移置必填欄位
    ele.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  useEffect(() => {
    const cost = _.get(
      models,
      `${isTwoLayerListing ? '[0].items[0].cost' : '[0].cost'}`,
      0
    )
    change('product.cost', cost)
    change('shareMediaBetweenModels', shareMediaBetweenModels)
  }, [change, shareMediaBetweenModels, isTwoLayerListing, models])

  const prevVisibleRef = useRef()
  useEffect(() => {
    // 每次打開新增商品 modal 初始化表單
    if (visible && prevVisibleRef.current !== visible) {
      const isUpdateMode = !_.isEmpty(products)
      const newProduct = getNewProductWithCallback(initiallyFetchedListing, sortList, dataMap, form)
      const newProducts = isUpdateMode
        // 如果是修改剛新增的商品則帶入商品資料
        ? _.map(products, getUpdatedProduct(initiallyFetchedListing, form))
        // 新增商品的話帶入預設值
        : [newProduct]
      updateModels(newProducts)
      change('isUpdateMode', isUpdateMode)
      setImageTableKey(Date.now())
    }
    prevVisibleRef.current = visible
  }, [visible, change, setImageTableKey, getNewProductWithCallback, updateModels, products, isTwoLayerListing, initiallyFetchedListing, dataMap, sortList, form])

  const customColsProps = {
    listingName,
    shareMediaBetweenModels,
    specDetailMapBySpec,
    dataMap,
    change,
    submitFailed,
    deleteProduct,
    setImageTableKey
  }

  return (
    <Modal
      title={`${_.isEmpty(products) ? '新增' : readOnly ? '檢視' : '修改'}屬性商品`}
      className={cx('new-product-modal', styles.fullScreenModal)}
      {...props}
      okButtonDisabled={isMediaUploading}
      onOk={submitProduct}
    >
      {
        _.isNull(specDetailMapBySpec)
          ? <FormLoader loaded={false} />
          : (
            <FormValueSelector
              selector={select => ({
                isUpdateMode: select('isUpdateMode'),
                models: select('models'),
                shareMediaBetweenModels: select('shareMediaBetweenModels')
              })}
              withoutPrefix
            >
              {(formValues = {}) => {
                const {
                  isUpdateMode,
                  models: modelsFromFormValues = [],
                  shareMediaBetweenModels: shareMediaBetweenModelsFromFormValues
                } = formValues
                return (
                  <>
                    <ImageFunctionTable
                      key={imageTableKey}
                      models={modelsFromFormValues}
                      modelsFieldName='models'
                      customImageFieldName={PRODUCT_KEY.NEW_PRODUCT_IMAGES}
                      shareImageFieldName='shareMediaBetweenModels'
                      syncImageFieldName='syncProductImages'
                      modelSpecHeader={listingSpecName}
                      itemSpecHeader={listingItemSpecName}
                      shareMediaBetweenModels={shareMediaBetweenModelsFromFormValues}
                      customProductIdColHeader={(
                        <Col span={3}>
                          <p>商品編號</p>
                          <p>商品名稱</p>
                        </Col>
                      )}
                      customColHeaders={(
                        <>
                          <Col span={readOnly ? 8 : 10}>
                            <p>
                              {`${listingSpecName} / `}
                              <ProductNameTitleWtihPopover />
                            </p>
                            {isTwoLayerListing && (
                              <p>
                                {`${listingItemSpecName} / `}
                                <ProductNameTitleWtihPopover />
                              </p>
                            )}
                          </Col>
                          <Col span={4}>
                            <YPopover
                              placement='bottom'
                              content={(
                                <FormValueSelector
                                  fieldName='product.cost'
                                  withoutPrefix
                                >
                                  {cost => (
                                    <>
                                      備貨數量
                                      <br />
                                      請留意成本與數量限制：
                                      <br />
                                      <br />
                                      {`• 成本：${numeralFormat(cost, { format: '0', allowZero: true })}`}
                                      <br />
                                      <LimitDescBtwCostAndStock />
                                    </>
                                  )}
                                </FormValueSelector>
                              )}
                            >
                              備貨數量
                              <Information className={styles.info} />
                            </YPopover>
                          </Col>
                          <Col span={5}>
                            <YPopover
                              content={(
                                <>
                                  網站/搜尋引擎搜尋商品時使用，
                                  <br />
                                  請確認填入正確以利精準搜尋
                                </>
                              )}
                            >
                              實際國際條碼
                              <Information className={styles.info} />
                              <span
                                key='sub'
                                className={styles.sub}
                              >
                                {'(非必填）'}
                              </span>
                            </YPopover>
                            <br />
                            <YPopover
                              content={(
                                <>
                                  同MPN(製造商零件編號)
                                  <br />
                                  訂單成立時, 只會帶入各屬性項目所輸入的供應商商品料號
                                </>
                              )}
                            >
                              商品料號
                              <Information className={styles.info} />
                              <span
                                key='sub'
                                className={styles.sub}
                              >
                                {'(非必填）'}
                              </span>
                            </YPopover>
                          </Col>
                          {
                            !readOnly && (
                              <Col span={1} />
                            )
                          }
                        </>
                      )}
                      customProductIdCol={props => CustomProductIdCol({ ...props, products })}
                      customCols={props => CustomCols({ ...props, ...customColsProps })}
                      defaultShowUploader={false}
                      readOnly={readOnly}
                      isNoModelsListing={false}
                      withProductIdCol={readOnly}
                      withCustomProductIdCol={readOnly}
                      withCustomCols
                      isHiddenCounterOnInvalid
                    />
                    {(!isUpdateMode && !readOnly) && (
                      <NewProductBtn
                        onClick={addNewProduct(modelsFromFormValues)}
                        className={styles.addNewProductBtn}
                      />
                    )}
                  </>
                )
              }}
            </FormValueSelector>
          )
      }
    </Modal>
  )
}

const NewProductModelWithUIState = UiStateContainer.createUi(NewProductModel, defaultUiContextCosumer)

export default formDecorator(FORM_CONFIG)(NewProductModelWithUIState)
